import { CometChatCalls } from '@cometchat/calls-sdk-javascript';
import { CometChatUIKit } from '@cometchat/chat-uikit-react';
import React, { useState } from 'react';
import { COMETCHAT_CONSTANTS } from './AppConstants';
import googleLogo from './assets/google.svg';
import './CometChatLoginWithGoogle.css';
import { LoginBrand } from './components/LoginBrand/LoginBrand';
import {
  signInAnonymouslyFirebase,
  signInWithGoogle,
  signInWithGoogleRedirect,
} from './firebase';
import { initCallsSDK } from './utils/init-calls-sdk';

interface LoginWithFirebaseProps {
  onLoginSuccess: () => void;
}

const LoginWithFirebase: React.FC<LoginWithFirebaseProps> = ({
  onLoginSuccess,
}) => {
  // Which sign-in is running. Firebase plus the CometChat logins take a few
  // seconds, so the pressed button shows a spinner and both stay disabled;
  // without it people assumed nothing happened and clicked again.
  const [pendingMethod, setPendingMethod] = useState<'google' | 'guest' | null>(
    null,
  );
  const isLoading = pendingMethod !== null;
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);

  /**
   * The server refuses a user name longer than this
   * ("The name must not be greater than 100 characters."), as Android and iOS
   * cap it too (ENG-40315).
   */
  const MAX_NAME_LENGTH = 100;

  /**
   * What a failed sign-in says. Every path used to only `console.log`, so the
   * spinner stopped and the screen said nothing — a guest name over the limit
   * and signing in offline both failed silently (ENG-40315, ENG-40318).
   */
  const signInError = (reason: unknown): string => {
    const e = reason as { code?: string; message?: string } | undefined;
    if (!navigator.onLine || e?.code === 'auth/network-request-failed') {
      return "Couldn't sign in. Check your internet connection.";
    }
    if (e?.code === 'auth/operation-not-allowed') {
      return "Guest sign-in is turned off for this app.";
    }
    if (e?.code === 'auth/too-many-requests') {
      return 'Too many attempts. Try again in a minute.';
    }
    if (typeof e?.message === 'string' && /"name"/.test(e.message)) {
      return `Use a name of ${MAX_NAME_LENGTH} characters or fewer.`;
    }
    return "Couldn't sign in. Try again.";
  };

  const createCometChatUser = async (
    uid: string,
    name: string,
    avatar?: string,
  ): Promise<void> => {
    const url = `https://${COMETCHAT_CONSTANTS.APP_ID}.api-${COMETCHAT_CONSTANTS.REGION.toLowerCase()}.${COMETCHAT_CONSTANTS.IS_STAGING ? 'cometchat-staging.com' : 'cometchat.io'}/v3/users`;

    const payload: any = {
      uid,
      name,
    };

    if (avatar) {
      payload.avatar = avatar;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        appId: COMETCHAT_CONSTANTS.APP_ID,
        apiKey: COMETCHAT_CONSTANTS.AUTH_KEY,
      },
      body: JSON.stringify(payload),
    });

    console.log('User creation response status:', response.status);
    console.log(
      'User creation response headers:',
      Object.fromEntries(response.headers.entries()),
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('User creation failed:', errorText);

      // Check if user already exists
      if (response.status === 409) {
        console.log('User already exists, continuing...');
        return; // User already exists, that's fine
      }

      throw new Error(
        `User creation failed: ${response.statusText} - ${errorText}`,
      );
    }

    const responseData = await response.json();
    console.log('User creation response:', responseData);
  };

  const loginToCometChat = async (
    uid: string,
    name: string,
    avatar?: string,
  ): Promise<void> => {
    try {
      await CometChatUIKit.login(uid);
      // UIKit login re-inits CometChatCalls without our hosts; restore them
      await initCallsSDK();
      await CometChatCalls.login(uid, COMETCHAT_CONSTANTS.AUTH_KEY);
      onLoginSuccess();
    } catch (error: any) {
      console.log('Initial login failed, error details:', {
        error,
        errorCode: error?.errorCode,
        errorDescription: error?.errorDescription,
        message: error?.message,
        code: error?.code,
      });

      // If user doesn't exist, create the user first
      if (
        error &&
        (error.code === 'ERR_UID_NOT_FOUND' ||
          error.errorCode === 'ERR_UID_NOT_FOUND' ||
          error.message?.includes('UID_NOT_FOUND') ||
          (error.errorCode === 'ERROR_UNKNOWN' &&
            error.errorDescription?.includes('Login failed'))) // Handle unknown errors specifically for login failure
      ) {
        try {
          await createCometChatUser(uid, name, avatar);
          console.log('CometChat user created successfully, retrying login...');

          // Add a small delay to ensure user is properly created
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Retry login after user creation
          await CometChatUIKit.login(uid);

          // UIKit login re-inits CometChatCalls without our hosts; restore them
          await initCallsSDK();
          await CometChatCalls.login(uid, COMETCHAT_CONSTANTS.AUTH_KEY);
          onLoginSuccess();
        } catch (creationError) {
          console.error('Error creating CometChat user:', creationError);
          throw new Error('Failed to create user account. Please try again.');
        }
      } else {
        console.error('Error during CometChat login:', error);
        throw new Error(
          `Login failed: ${error?.errorDescription || error?.message || 'Unknown error'}`,
        );
      }
    }
  };

  const handleGoogleLogin = async () => {
    if (isLoading) {
      return;
    }
    setPendingMethod('google');
    try {
      console.log('Starting Google login...');
      const result = await signInWithGoogle();
      const user = result.user;

      if (user) {
        const uid = user.uid;
        const name = user.displayName || user.email || 'Google User';
        const avatar = user.photoURL || undefined;

        console.log('Google login successful:', {
          uid,
          name,
          email: user.email,
          avatar,
        });

        await loginToCometChat(uid, name, avatar);
      } else {
        throw new Error('Failed to get user from Google login');
      }
    } catch (reason) {
      console.error('Google login failed:', reason);
      const code = (reason as { code?: string } | undefined)?.code;
      // The browser blocked the popup, or the user dismissed it. A popup that
      // never opened used to fail silently (ENG-40316): fall back to the
      // redirect flow, which needs no popup.
      if (
        code === 'auth/popup-blocked' ||
        code === 'auth/cancelled-popup-request'
      ) {
        try {
          await signInWithGoogleRedirect();
          return; // the page navigates away
        } catch (redirectReason) {
          console.error('Google redirect sign-in failed:', redirectReason);
          setError(signInError(redirectReason));
        }
      } else if (code === 'auth/popup-closed-by-user') {
        setError(null); // they closed it on purpose
      } else {
        setError(signInError(reason));
      }
    } finally {
      setPendingMethod(null);
    }
  };

  const handleAnonymousLogin = async () => {
    // The guest pill's wrapper div is clickable around the disabled button.
    if (isLoading || !username.trim()) {
      return;
    }

    setError(null);
    setPendingMethod('guest');
    try {
      console.log('Starting anonymous login with username:', username.trim());

      // First, sign in anonymously with Firebase
      const result = await signInAnonymouslyFirebase();
      console.log('Firebase anonymous login result:', result);

      const user = result.user;

      if (user) {
        const uid = user.uid;
        const name = username.trim();

        console.log('Firebase anonymous login successful:', {
          uid,
          name,
          isAnonymous: user.isAnonymous,
          email: user.email,
        });

        // Now login to CometChat with the Firebase UID
        await loginToCometChat(uid, name);
      } else {
        throw new Error(
          'Failed to create anonymous user - no user returned from Firebase',
        );
      }
    } catch (reason) {
      console.error('Anonymous login failed:', reason);
      setError(signInError(reason));
    } finally {
      setPendingMethod(null);
    }
  };
  return (
    <div className="login-split">
      <LoginBrand />

      <div className="cometchat-login-with-google__wrapper">
      <div className="cometchat-login-with-google">
        <div className="login-form__heading">
          <h2 className="login-form__title">Sign in</h2>
          <p className="login-form__subtitle">
            Sign in with Google to keep your account and call history, or
            join as a guest for a quick call.
          </p>
        </div>

        {/* Google leads because it is the account that persists: web, iOS and
            Android share one Firebase project, so it is the same user and
            call history everywhere. A real <button> -- this used to be a div
            with onClick, which keyboard users could not reach or activate,
            meaning they could not sign in with Google at all. */}
        <button
          type="button"
          className="cometchat-login-with-google__click login-form__google"
          onClick={handleGoogleLogin}
          disabled={isLoading}
          aria-busy={pendingMethod === 'google'}>
          {pendingMethod === 'google' ? (
            <span className="login-busy" role="status">
              <span className="login-spinner" aria-hidden="true" />
              <span className="cometchat-login-with-google__text">
                Signing in…
              </span>
            </span>
          ) : (
            <>
              {/* Decorative: the label already says "Google". */}
              <img src={googleLogo} alt="" />
              <span className="cometchat-login-with-google__text">
                Continue with Google
              </span>
            </>
          )}
        </button>

        <div className="cometchat-login-with-google__separator-wrapper">
          {/* Below 900px there is no GUEST label, so the divider names the option. */}
          <span className="cometchat-login-with-google__separator">
            or<span className="login-narrow-only"> join as a guest</span>
          </span>
        </div>

        <div className="cometchat-login-with-google__input-wrapper">
          <label className="login-form__option-label" htmlFor="guest-display-name">
            Guest
          </label>
          <input
            id="guest-display-name"
            type="text"
            autoComplete="nickname"
            aria-describedby="guest-note"
            placeholder="Your display name"
            value={username}
            maxLength={MAX_NAME_LENGTH}
            onChange={e => {
              setUsername(e.target.value.slice(0, MAX_NAME_LENGTH));
              setError(null);
            }}
            className="cometchat-login-with-google__input"
            onKeyDown={e => {
              if (e.key === 'Enter' && !isLoading && username.trim()) {
                handleAnonymousLogin();
              }
            }}
          />
          <div
            onClick={handleAnonymousLogin}
            style={{
              cursor:
                pendingMethod === 'guest'
                  ? 'progress'
                  : isLoading || !username.trim()
                    ? 'not-allowed'
                    : 'pointer',
            }}
            className={`cometchat-login-with-google__click cometchat-login-with-google__click-continue ${isLoading || !username.trim() ? 'disabled' : ''}`}>
            <button
              className="cometchat-login__text"
              disabled={isLoading || !username.trim()}
              aria-busy={pendingMethod === 'guest'}>
              {pendingMethod === 'guest' ? (
                <span className="login-busy" role="status">
                  <span className="login-spinner" aria-hidden="true" />
                  Signing in…
                </span>
              ) : (
                'Continue as guest'
              )}
            </button>
          </div>
          {error && (
            <p className="cometchat-login-with-google__error" role="alert">
              {error}
            </p>
          )}
          {/* A guest is an anonymous Firebase user with a random id. It
              survives reloads on this browser, but signing out discards it,
              so the next guest sign-in is a different user and the old call
              history is out of reach. Say so before someone relies on it. */}
          <p className="login-form__note" id="guest-note">
            <span className="login-wide-only">
              Guest sessions stay on this browser and end when you sign out,
              along with their call history.
            </span>
            <span className="login-narrow-only">
              Guest history is cleared when you sign out.
            </span>
          </p>
        </div>
      </div>
      </div>
    </div>
  );
};

export default LoginWithFirebase;
